import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import { disabledFormInputStyles } from '@Components/Form/styles';
import { useNatureOfBusinessForm } from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider';
import {
  OTHER_INCOME_STRUCTURE_ID,
  INCOME_SUBGROUP_CATEGORY,
} from 'src/Enum/IncomeStructure';
import {
  IIncomeStructureFormValues,
  IIncomeCategoryFormValues,
} from '../../../NatureOfBusinessFormProvider/types';
import { ProductIncomeModalSchema } from './validators';
import { createNewExtendedProductIncome } from './utils';
import {
  setEmptyYearFields,
  mapIncomeCategoryToFormValues,
  updateIncomeCategory,
} from '../../utils';

type ProductIncomeModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  close: () => void;
  keyItem?: string;
  editData?: IIncomeCategoryFormValues;
};

const ProductIncomeModal = ({
  open,
  mode,
  close,
  keyItem,
  editData,
}: ProductIncomeModalProps) => {
  const initialValue =
    mode === 'edit' ? mapIncomeCategoryToFormValues(editData) : undefined;

  const productIncomeModalContext = useForm<IIncomeCategoryFormValues>({
    defaultValues: initialValue,
    mode: 'onChange',
    resolver: zodResolver(ProductIncomeModalSchema),
  });

  const { getValues, handleSubmit, reset, formState } =
    productIncomeModalContext;
  const { control, setValue } = useNatureOfBusinessForm();

  const incomeStructure = useWatch({
    control,
    name: 'incomeStructure',
  }) as IIncomeStructureFormValues;

  const productIncomeItems = incomeStructure?.productIncomeItems || {};
  const productIncomePcts = incomeStructure?.productIncomePercentages || {};

  const { isCompletedTh = false, isCompletedEn = false } =
    incomeStructure?.completedSection ?? {};

  const handleClose = () => {
    reset();
    close();
  };

  const handleSave = () => {
    const formValues = getValues();

    if (mode === 'create') {
      const prevExtendedItems = Object.values(productIncomeItems).filter(
        (item) =>
          item.incomeCategory?.subGroup ===
          INCOME_SUBGROUP_CATEGORY.PRODUCT_INCOME
      );
      const nextSequence =
        prevExtendedItems.reduce(
          (max, item) => Math.max(max, item.incomeCategory?.sequence ?? 0),
          0
        ) + 1;
      const newItemKey = `${INCOME_SUBGROUP_CATEGORY.PRODUCT_INCOME}-${nextSequence}`;
      const newExtendedItem = createNewExtendedProductIncome(
        formValues,
        nextSequence
      );
      const productIncomeOtherItem =
        productIncomeItems[OTHER_INCOME_STRUCTURE_ID.OTHER_PRODUCT_INCOME];

      setValue(
        `incomeStructure.productIncomeItems.${newItemKey}`,
        newExtendedItem
      );
      setValue(
        `incomeStructure.productIncomePercentages.${newItemKey}`,
        newExtendedItem
      );
      setValue(
        `incomeStructure.productIncomeItems.${OTHER_INCOME_STRUCTURE_ID.OTHER_PRODUCT_INCOME}`,
        setEmptyYearFields(productIncomeOtherItem)
      );
    }

    if (mode === 'edit' && keyItem) {
      const prevExtendedItem = productIncomeItems[keyItem].incomeCategory;
      const prevExtendedPct = productIncomePcts[keyItem].incomeCategory;
      const updateExtendedItem = updateIncomeCategory(
        formValues,
        prevExtendedItem
      );
      const updateExtendedPct = updateIncomeCategory(
        formValues,
        prevExtendedPct
      );

      setValue(
        `incomeStructure.productIncomeItems.${keyItem}.incomeCategory`,
        updateExtendedItem
      );
      setValue(
        `incomeStructure.productIncomePercentages.${keyItem}.incomeCategory`,
        updateExtendedPct
      );
    }

    handleClose();
  };

  return (
    <FormProvider {...productIncomeModalContext}>
      <Dialog open={open} maxWidth="md">
        <DialogTitle fontWeight={800} fontSize={16}>
          {mode === 'create' ? 'เพิ่ม' : 'แก้ไข'}
          รายการสายผลิตภัณฑ์หรือกลุ่มธุรกิจ
        </DialogTitle>
        <DialogContent>
          <Flex flexDirection={'column'} gap={2} mt={1}>
            <FormInput
              name="nameTh"
              label="ชื่อสายผลิตภัณฑ์หรือกลุ่มธุรกิจ (ไทย)"
              placeholder="กรุณาระบุชื่อสายผลิตภัณฑ์หรือกลุ่มธุรกิจ (ไทย)"
              required
              sx={isCompletedTh ? disabledFormInputStyles : undefined}
            />
            <FormInput
              name="nameEn"
              label="ชื่อสายผลิตภัณฑ์หรือกลุ่มธุรกิจ (อังกฤษ)"
              placeholder="กรุณาระบุชื่อสายผลิตภัณฑ์หรือกลุ่มธุรกิจ (อังกฤษ)"
              required
              sx={isCompletedEn ? disabledFormInputStyles : undefined}
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit(handleSave)}
            disabled={!formState.isValid}
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default ProductIncomeModal;
